<template>
  <v-data-table
    :headers="headers"
    :items="filteredUsers"
    sort-by="brand"
    :items-per-page.sync="itemsPerPage"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-text-field 
          v-model="search" 
          hide-details 
          prepend-inner-icon="search" 
          single-line 
          outlined 
          dense 
          :placeholder="$t('Users.List.searchText')"
          style="max-width: 200px"
        >
        </v-text-field>
        <DataFetcher v-slot:default="{ items }" endpoint="/malls" class="mx-5">
          <v-select 
            v-model="selectedMall" 
            :items="items" 
            :label="$t('Calendar.Label.mall')" 
            item-text="name" 
            color="primary" 
            value="id" 
            return-object 
            hide-details
            style="max-width: 200px"
            clearable
            outlined
            dense
          ></v-select>
        </DataFetcher>
        <v-select 
          v-model="selectedFrequency" 
          :items="frequencyFilters" 
          :label="$t('Users.Form.inputFrequency')" 
          color="primary" 
          hide-details
          style="max-width: 200px"
          class="mr-5"
          clearable
          outlined
          dense
        ></v-select>
        <v-select 
          v-model="selectedIsActive" 
          :items="isActiveFilters" 
          color="primary" 
          hide-details
          style="max-width: 100px"
          class="mr-5"
          outlined
          dense
        ></v-select>
        <v-spacer></v-spacer>
        <v-dialog v-model="showEditor" max-width="1000px" persistent>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" dark class="mb-2" v-on="on">{{$t('Users.Button.addBrand')}}</v-btn>
          </template>
          <UserEditor 
            v-if="showEditor"
            ref="userEditor"
            :defaultUser="editedIndex >= 0 ? usersList[editedIndex] : null" 
            @save="save"
            @cancel="close"
            @invalid-form="$store.dispatch('errorMessage', $t('Message.Error.invalidForm'))"
          />
        </v-dialog>
      </v-toolbar>
      
    </template>
    <template v-slot:item.avatar="{ item }">
      <v-img v-if="item.picture" height="25px" width="25px" :src="getPicturePath(item.picture)"></v-img>
      <v-icon v-else>store</v-icon>
    </template>
    <template v-slot:item.fullName="{ item }">
      {{item.firstName + ' ' + item.lastName}}
    </template>
    <template v-slot:item.inputFrequency="{ item }">
      {{frequencyFilters.find(freq => freq.value === item.inputFrequency).text}}
    </template>
    <template v-slot:item.openingDate="{ item }">
      {{new Intl.DateTimeFormat('fr-FR').format(new Date(item.openingDate))}}
    </template>
    <template v-slot:item.closingDate="{ item }">
      {{item.closingDate && new Intl.DateTimeFormat('fr-FR').format(new Date(item.closingDate))}}
    </template>
    <template v-slot:item.isActive="{ item }">
      <v-chip :color="item.isActive ? 'success' : 'error'">{{item.isActive ? 'Actif' : 'Inactif'}}</v-chip>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-btn fab small icon @click="editItem(item)" color="secondary">
        <v-icon small>edit</v-icon>
      </v-btn>
      <v-btn fab small icon @click="deleteItem(item)" color="secondary">
        <v-icon small>delete</v-icon>
      </v-btn>
    </template>
  </v-data-table>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import UserEditor from '@/components/User/UserEditor'
import DataFetcher from '@/components/Utils/DataFetcher'
import UserHelpersMixin from '@/mixins/UserHelpersMixin'
import FilesPathHelpersMixin from '@/mixins/FilesPathHelpersMixin'
import { frequencies } from '@/config/fixedValues'
export default {
  components: { UserEditor, DataFetcher },
  mixins: [UserHelpersMixin, FilesPathHelpersMixin],
  data: () => ({
    itemsPerPage: 15,
    showEditor: false,
    editedIndex: -1,
    search: '',
    selectedMall: null,
    selectedFrequency: null,
    selectedIsActive: true,
    frequencyFilters: frequencies,
    isActiveFilters: [
      {text: 'Actif', value: true},
      {text: 'Inactif', value: false},
      {text: 'Tous', value: null}
    ]
  }),

  computed: {
    ...mapState('user', [
      'usersList'
    ]),
    ...mapState('auth', [
      'user'
    ]),
    headers () {
      return [
        { sortable: false, value: 'avatar', width: '50px' },
        { text: 'ID', value: 'id', align: 'right' },
        { text: this.$t('Users.Form.brand'), value: 'brand' },
        { text: this.$t('Users.Form.mall'), value: 'mall.name' },
        { text: 'Responsable', value: 'fullName' },
        { text: 'Saisie', value: 'inputFrequency' },
        { text: 'Ouverture', value: 'openingDate' },
        { text: 'Fermeture', value: 'closingDate' },
        { sortable: false, value: 'isActive', width: '50px' },
        { sortable: false, value: 'actions', width: '125px' },
      ]
    },
    filteredUsers () {
      return this.usersList.filter(user => {
        return !this.isAdmin(user) &&
        (
          (user.email && user.email.match(RegExp(this.search, "i"))) || 
          (user.firstName && user.firstName.match(RegExp(this.search, "i"))) || 
          (user.lastName && user.lastName.match(RegExp(this.search, "i"))) || 
          (user.brand && user.brand.match(RegExp(this.search, "i")))
        )
        && (this.selectedMall ? user.mall.id === this.selectedMall.id : true)
        && (this.selectedFrequency ? user.inputFrequency === this.selectedFrequency : true)
        && (this.selectedIsActive !== null ? user.isActive === this.selectedIsActive : true)
      })
    }
  },

  watch: {
    showEditor (val) {
      val || this.close()
    }
  },

  created () {
    this.initialize()
  },

  methods: {
    ...mapActions('user', [
      'loadUsers',
      'createUser',
      'updateUser',
      'deleteUser'
    ]),
    async initialize () {
      this.selectedMall = this.user.mall
      this.loadUsers()
    },

    editItem (item) {
      this.editedIndex = this.usersList.indexOf(item)
      this.showEditor = true
    },

    async deleteItem (item) {
      confirm(this.$t('Users.Message.delete')) && this.deleteUser(item.id)
    },

    close () {
      this.showEditor = false
      this.$refs.userEditor.resetForm()
      setTimeout(() => {
        this.editedIndex = -1
      }, 300)
    },

    async save (user) {
      if (this.editedIndex > -1) {
        try {
          await this.updateUser(user)
          this.close()
        } catch (error) {
          this.$store.dispatch('errorMessage', this.$t('Message.Error.update'))
        }
      } else {
        try {
          await this.createUser(user)
          this.close()
        } catch (error) {
          error === 'users limit reached' 
          ? this.$store.dispatch('errorMessage', this.$t('Users.Message.limitReached'))
          : this.$store.dispatch('errorMessage', this.$t('Message.Error.create'))
        }
      }
    }
  }
}
</script>